import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { salaNoCelularDisponivel } from '@/features/salaNoCelular/salaNoCelular';
import { ponteDoAplicativo } from './aplicativo';
import { assinar, contarAoPortal, encerrarNoAparelho, lerFila, salasEsquecidas, ultimaNoticia } from './encerrarSala';
import { salasDoAparelho } from './salaNoAparelho';

/**
 * A NOTÍCIA DAS SALAS AO PORTAL, e o AVISO DA SALA ESQUECIDA (03/11/2026; a lógica em `encerrarSala.ts`). Só no
 * aplicativo (computador e celular/iPad) e só para quem prepara sala; no navegador nada acontece.
 */
const INTERVALO_MS = 60_000;

/** O "Encerrar sala" está liberado para esta conta? (a última notícia diz) */
export function useEncerrarSalaLiberado(): boolean {
  return useSyncExternalStore(assinar, () => ultimaNoticia().encerrarSala, () => false);
}
/** A fila "encerrar no portal" deste aparelho (as salas encerradas aqui sem internet). */
export function useFilaDeEncerramento() {
  return useSyncExternalStore(assinar, () => JSON.stringify(lerFila()), () => '[]');
}

const noAplicativo = () => !!ponteDoAplicativo() || salaNoCelularDisponivel();

export function NoticiaDasSalas() {
  const { user, isAuthenticated } = useAuth();
  const papel = (user as { role?: string } | null)?.role;
  const qc = useQueryClient();
  const rodando = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !user || !['TEACHER', 'SCHOOL_STAFF', 'ADMIN'].includes(String(papel)) || !noAplicativo()) return;
    const rodar = async () => {
      if (rodando.current) return;
      rodando.current = true;
      try { await contarAoPortal(Number(user.id)); void qc.invalidateQueries({ queryKey: ['salas-nao-encerradas'] }); }
      catch { /* sem internet ou portal fora: tenta no próximo minuto */ }
      finally { rodando.current = false; }
    };
    const primeira = setTimeout(rodar, 3000);
    const vez = setInterval(rodar, INTERVALO_MS);
    window.addEventListener('online', rodar);
    const volta = App.addListener('resume', () => { void rodar(); });
    return () => { clearTimeout(primeira); clearInterval(vez); window.removeEventListener('online', rodar); void volta.then((h) => h.remove()).catch(() => {}); };
  }, [isAuthenticated, user, papel, qc]);
  return null;
}

/** O aviso no topo: "Você tem uma sala de <simulado> que não foi encerrada", com "Encerrar sala". */
export function AvisoDeSalaEsquecida() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const liberado = useEncerrarSalaLiberado();
  const noticia = useSyncExternalStore(assinar, ultimaNoticia, ultimaNoticia);
  const { data: salas } = useQuery({ queryKey: ['salas-do-aparelho'], queryFn: salasDoAparelho, enabled: liberado && noAplicativo(), refetchInterval: 15000, staleTime: 5000 });
  const [confirmar, setConfirmar] = useState<number | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  if (!liberado || !user || !salas?.length) return mensagem ? <p className="mb-4 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700" role="status">{mensagem}</p> : null;
  const esquecidas = salasEsquecidas(salas, noticia.salas, Number(user.id));
  if (!esquecidas.length && !mensagem) return null;
  const encerrar = async (i: number) => {
    const { sala } = esquecidas[i];
    setOcupado(true);
    const abrir = () => { if (ponteDoAplicativo()) void ponteDoAplicativo()?.abrirSala?.(sala.id); else navigate(`/teacher/sala-no-celular?sala=${sala.id}&simulado=${sala.simuladoId}`); };
    const r = await encerrarNoAparelho(sala, Number(user.id), abrir).catch((e) => ({ ok: false as const, texto: (e as Error).message }));
    setOcupado(false); setConfirmar(null); setMensagem(r.texto);
    void qc.invalidateQueries({ queryKey: ['salas-do-aparelho'] });
  };
  return (
    <div className="mb-4 flex flex-col gap-2" data-testid="aviso-sala-esquecida">
      {esquecidas.map(({ sala, titulo }, i) => (
        <div key={sala.id} className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900" role="alert">
          <p>Você tem uma sala de <strong>{titulo}</strong> que não foi encerrada. O prazo do simulado já passou.</p>
          {confirmar === i ? (
            <p className="mt-2 flex flex-wrap items-center gap-2">
              <span>Encerrar sem aplicar? A prova sai deste aparelho e a sala fica encerrada no portal. Resultados a enviar vão antes.</span>
              <button type="button" disabled={ocupado} className="min-h-[40px] rounded-md border border-red-300 bg-white px-3 font-medium text-red-800 disabled:opacity-60" onClick={() => void encerrar(i)} data-testid={`confirmar-encerrar-esquecida-${sala.id}`}>{ocupado ? 'Encerrando…' : 'Encerrar sala'}</button>
              <button type="button" disabled={ocupado} className="min-h-[40px] rounded-md border border-gray-300 bg-white px-3" onClick={() => setConfirmar(null)}>Cancelar</button>
            </p>
          ) : (
            <button type="button" className="mt-2 min-h-[40px] rounded-md border border-amber-400 bg-white px-3 font-medium text-amber-900" onClick={() => { setMensagem(null); setConfirmar(i); }} data-testid={`encerrar-esquecida-${sala.id}`}>Encerrar sala</button>
          )}
        </div>
      ))}
      {mensagem && <p className="text-sm text-gray-700" role="status">{mensagem}</p>}
    </div>
  );
}
